/**
 * Choose Spot Month dialog — Access `frmReportExposureQrySpotMonthAdd`.
 * btnSpotMonth opens it pre-filled with the current spot month (DLookup full_symbol
 * where is_spot_month=1). Save flips market_calendar.is_spot_month to the chosen
 * full_symbol. (The quality_input/quality_output combos in Access are decorative —
 * btnSave only validates & persists futures_month — so they are omitted.)
 * btnSave/btnCancel live in the Access FormHeader → DialogTitle (right).
 */
import { Autocomplete, Dialog, DialogContent, TextField } from '@mui/material'
import DialogTitleBar from '../../../components/DialogTitleBar'
import { useEffect, useState } from 'react'
import { useSnackbar } from '../../../hooks/useSnackbar'
import {
  useGetFuturesMonthOptionsQuery,
  useGetSpotMonthQuery,
  useSetSpotMonthMutation,
} from '../../../store/api/reportExposureApi'

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
}

export default function ChooseSpotMonthDialog({ open, onClose, onSaved }: Props) {
  const { data: optionsData } = useGetFuturesMonthOptionsQuery(undefined, { skip: !open })
  const { data: spot } = useGetSpotMonthQuery(undefined, { skip: !open })
  const [setSpotMonth, { isLoading: saving }] = useSetSpotMonthMutation()
  const [value, setValue] = useState<string | null>(null)
  const snack = useSnackbar()

  useEffect(() => {
    if (open) setValue(spot?.futuresMonth ?? null)
  }, [open, spot?.futuresMonth])

  // Inject saved value into options (lookup may not include current spot month).
  const baseOptions = optionsData?.items.map((o) => o.value) ?? []
  const options = value && !baseOptions.includes(value) ? [value, ...baseOptions] : baseOptions

  const handleClose = () => {
    if (saving) return
    onClose()
  }

  const handleSave = async () => {
    if (saving) return
    if (!value) {
      snack.error('Please choose Futures Month!')
      return
    }
    try {
      await setSpotMonth({ futuresMonth: value }).unwrap()
      snack.success('Successfully Updated')
      onSaved()
      onClose()
    } catch (err: unknown) {
      const detail = (err as { data?: { detail?: string } })?.data?.detail
      snack.error(detail ?? 'Could not update spot month.')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (saving || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitleBar
        title="Exposure Report - Choose Spot Month"
        onSave={handleSave}
        onCancel={handleClose}
        saving={saving}
        saveDisabled={saving}
      />
      <DialogContent dividers>
        <Autocomplete
          options={options}
          value={value}
          onChange={(_e, v) => setValue(v)}
          isOptionEqualToValue={(a, b) => a === b}
          renderInput={(params) => (
            <TextField {...params} label="Choose Spot Month" required size="small" InputLabelProps={{ shrink: true }} />
          )}
          sx={{ mt: 0.5 }}
        />
      </DialogContent>
    </Dialog>
  )
}
